import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, List, Paper, Grid, Chip, Divider, Tooltip } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';

import LocalizedDate from '../../../components/common/LocalizedDate';
import { CheckCircle as CompletedIcon, MoreHoriz as InProgressIcon, Info as AssignedIcon, ForwardToInbox as ForwardIcon, Cancel as RejectedIcon } from '@mui/icons-material';
import { getSafeColor } from '../../../utils/colorUtils';

const AssignedByMeList = ({ tasks = [], isLoading, onNavigate }) => {
  const { t } = useTranslation(['tasks', 'common', 'documents']);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography variant="body2" color="textSecondary">
          {t('common:loading', 'Loading...')}
        </Typography>
      </Box>
    );
  }

  if (!tasks.length) {
    return (
      <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 4 }}>
        {t('tasks:no_assigned_tasks', 'No tasks assigned by you')}
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 1, maxHeight: 300, overflowY: 'auto' }}>
      <List sx={{ padding: 0 }}>
        {tasks.map((task, index) => {
          const effectiveStatus = task.status;
          const statusColor = getSafeColor(effectiveStatus);
          let statusIcon;
          switch (effectiveStatus) {
            case 'ASSIGNED':
              statusIcon = <AssignedIcon fontSize="small" />;
              break;
            case 'IN_PROGRESS':
              statusIcon = <InProgressIcon fontSize="small" />;
              break;
            case 'COMPLETED':
              statusIcon = <CompletedIcon fontSize="small" />;
              break;
            case 'FORWARDED':
              statusIcon = <ForwardIcon fontSize="small" />;
              break;
            case 'REJECTED':
              statusIcon = <RejectedIcon fontSize="small" />;
              break;
            default:
              statusIcon = <AssignedIcon fontSize="small" />;
          }

          const isPastDue = task.due_date && new Date(task.due_date) < new Date();

          return (
            <React.Fragment key={task.id}>
              <Paper elevation={1} sx={{ mb: 1, p: 1, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onNavigate(task); }}>
                <Grid container spacing={1} alignItems="center">
                  {/* Left section */}
                    <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {statusIcon}
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {task.document?.title || t('tasks:untitled_document')}
                        </Typography>
                      {task.document?.reference_number && (
                        <Typography variant="caption" color="text.secondary">{task.document.reference_number}</Typography>
                      )}
                      <Chip label={t(`tasks:status_${effectiveStatus.toLowerCase()}`, effectiveStatus)} size="small" color={statusColor} />
                    </Box>

                    {/* Dates row */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <CalendarTodayIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('tasks:assigned_on')}: <LocalizedDate date={task.assigned_at} typographyProps={{ variant: 'caption'}} />
                        </Typography>
                      </Box>

                    {/* Meta row */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      {(() => {
                        const pri = task.document?.priority || task.priority;
                        return (
                          <>
                            <Tooltip title={t('tasks:priority')}>
                              <PriorityHighIcon fontSize="small" />
                            </Tooltip>
                            <Typography variant="body2" color="text.secondary">{pri || '-'}</Typography>
                          </>
                        );
                      })()}
                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                      <PersonIcon fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        {task.assignee?.full_name || task.assignee?.username || t('tasks:unknown')}
                          </Typography>
                        </Box>
                    </Grid>

                  {/* Notes full width */}
                  {task.notes && (
                    <Grid item xs={12} sx={{ mt: 1 }}>
                      <Box sx={{ px: 1.5, py: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">"{task.notes}"</Typography>
                      </Box>
                    </Grid>
                  )}
                  </Grid>
              </Paper>
              {index < tasks.length - 1 && <Divider variant="inset" component="li" sx={{ my: 1 }} />}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default AssignedByMeList;
